/** Panel lateral de ayuda: el tema de la pantalla, sus pestañas y ventanas, y el enlace a todos los temas. */
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { BookOpen, CircleHelp, Info, TriangleAlert, X } from 'lucide-react';
import { Fragment, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '@/lib/auth.tsx';
import { TEMA_GENERAL, useAyuda } from './contexto.tsx';
import { hijosDe, temaPorId, visiblePara } from './registro.ts';
import type { BloqueAyuda, TemaAyuda } from './tipos.ts';

/** Los nombres de botones y campos van entre ** en el texto: se muestran en negrita. */
export function TextoAyuda({ texto }: { texto: string }): ReactNode {
  const partes = texto.split(/\*\*(.+?)\*\*/g);
  return partes.map((p, i) => (i % 2 === 1 ? <strong key={i} className="font-semibold text-foreground">{p}</strong> : <Fragment key={i}>{p}</Fragment>));
}

function Bloque({ b }: { b: BloqueAyuda }) {
  if (b.tipo === 'nota') {
    const aviso = b.tono === 'aviso';
    return (
      <p className={`flex gap-2 rounded-lg px-3 py-2 text-sm ${aviso ? 'bg-amber-50 text-amber-900' : 'bg-muted/60'}`}>
        {aviso ? <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" /> : <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />}
        <span><TextoAyuda texto={b.texto} /></span>
      </p>
    );
  }
  const Lista = b.tipo === 'pasos' ? 'ol' : 'ul';
  const items = b.tipo === 'pasos' ? b.pasos : b.items;
  return (
    <section className="space-y-1.5">
      <h3 className="text-sm font-semibold">{b.titulo}</h3>
      <Lista className={`space-y-1 pl-5 text-sm text-muted-foreground ${b.tipo === 'pasos' ? 'list-decimal' : 'list-disc'}`}>
        {items.map((x, i) => (
          <li key={i}><TextoAyuda texto={x} /></li>
        ))}
      </Lista>
    </section>
  );
}

/** El contenido de un tema (lo usa el panel y la página de ayuda), con lo que el usuario puede ver. */
export function ContenidoTema({ tema, alAbrir }: { tema: TemaAyuda; alAbrir?: (id: string) => void }) {
  const { puede } = useAuth();
  const ver = (p: Parameters<typeof visiblePara>[0]) => visiblePara(p, (c, n) => puede(c, n));
  const relacionados = (tema.relacionados ?? []).map(temaPorId).filter((t): t is TemaAyuda => !!t && ver(t.permiso));
  return (
    <div className="space-y-4" data-testid={`ayuda-tema-${tema.id}`}>
      <p className="text-sm"><TextoAyuda texto={tema.resumen} /></p>
      {tema.bloques.filter((b) => ver(b.permiso)).map((b, i) => <Bloque key={i} b={b} />)}
      {tema.mensajes?.length ? (
        <section className="space-y-1.5">
          <h3 className="text-sm font-semibold">Si aparece este mensaje</h3>
          <ul className="space-y-1.5 text-sm">
            {tema.mensajes.map((m, i) => (
              <li key={i} className="rounded-lg border px-3 py-2">
                <p className="font-medium">«{m.mensaje}»</p>
                <p className="text-muted-foreground"><TextoAyuda texto={m.que_hacer} /></p>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {relacionados.length > 0 && alAbrir ? (
        <section className="space-y-1.5">
          <h3 className="text-sm font-semibold">También te puede servir</h3>
          <div className="flex flex-wrap gap-1.5">
            {relacionados.map((r) => (
              <button key={r.id} type="button" className="rounded-full border px-2.5 py-1 text-xs hover:bg-accent" onClick={() => alAbrir(r.id)} data-testid={`ayuda-ir-${r.id}`}>{r.titulo}</button>
            ))}
          </div>
        </section>
      ) : null}
      {tema.manual ? <p className="text-xs text-muted-foreground">Manual de usuario, sección {tema.manual}.</p> : null}
    </div>
  );
}

export function PanelAyuda() {
  const { abierto, abrir, cerrar } = useAyuda();
  const { puede } = useAuth();
  const navigate = useNavigate();
  const tema = temaPorId(abierto) ?? (abierto ? temaPorId(TEMA_GENERAL) : null);
  // Las pestañas y ventanas de la pantalla: el tema principal y sus hijos
  const principal = tema?.padre ? temaPorId(tema.padre) : tema;
  const familia = principal ? [principal, ...hijosDe(principal.id)].filter((t) => visiblePara(t.permiso, (c, n) => puede(c, n))) : [];
  return (
    <DialogPrimitive.Root open={!!tema} onOpenChange={(o) => !o && cerrar()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[60] bg-black/20" />
        <DialogPrimitive.Content className="fixed right-0 top-0 z-[60] flex h-full w-[440px] max-w-full flex-col border-l bg-card shadow-xl focus:outline-none" data-testid="panel-ayuda" aria-describedby={undefined}>
          {tema ? (
            <>
              <div className="flex items-start gap-2 border-b px-5 py-4">
                <CircleHelp className="mt-0.5 h-5 w-5 shrink-0 text-mg-verde" />
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Ayuda{principal && principal.id !== tema.id ? ` · ${principal.titulo}` : ''}</p>
                  <DialogPrimitive.Title className="text-lg font-semibold leading-tight">{tema.titulo}</DialogPrimitive.Title>
                </div>
                <DialogPrimitive.Close className="rounded-sm opacity-70 hover:opacity-100" aria-label="Cerrar la ayuda"><X className="h-4 w-4" /></DialogPrimitive.Close>
              </div>
              {familia.length > 1 ? (
                <div className="flex flex-wrap gap-1.5 border-b px-5 py-2.5" data-testid="ayuda-familia">
                  {familia.map((t) => (
                    <button key={t.id} type="button" className={`rounded-full px-2.5 py-1 text-xs ${t.id === tema.id ? 'bg-mg-verde text-white' : 'border hover:bg-accent'}`} onClick={() => abrir(t.id)} data-testid={`ayuda-ir-${t.id}`}>
                      {t.id === principal?.id ? 'La pantalla' : t.titulo}
                    </button>
                  ))}
                </div>
              ) : null}
              <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
                <ContenidoTema tema={tema} alAbrir={abrir} />
              </div>
              <div className="border-t px-5 py-3">
                <button type="button" className="inline-flex items-center gap-1.5 text-sm font-medium text-mg-verde hover:underline" onClick={() => { cerrar(); navigate('/ayuda'); }} data-testid="ayuda-todos">
                  <BookOpen className="h-4 w-4" /> Ver todos los temas de ayuda
                </button>
              </div>
            </>
          ) : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
